import React, { useState, useRef, useEffect } from "react";

interface Props {
  value: string;
  onChange: (newValue: string) => void;
  placeholder?: string;
}

export default function EditableLabel({
  value,
  onChange,
  placeholder = "Enter chat window name"
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select(); // Optional: selects the whole text
    }
  }, [isEditing]);

  const handleBlur = () => {
    setIsEditing(false);
    onChange(tempValue.trim() || value); // fallback if empty
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBlur();
    } else if (e.key === "Escape") {
      setTempValue(value);
      setIsEditing(false);
    }
  };

  return isEditing ? (
    <input
      ref={inputRef}
      type="text"
      aria-label={placeholder}
      className="text-lg font-medium ml-2 px-2 py-1 bg-transparent rounded-sm min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
      value={tempValue}
      onChange={(e) => setTempValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
    />
  ) : (
    <button
      type="button"
      aria-label={`Edit name: ${value || placeholder}`}
      className="text-lg font-medium ml-2 cursor-pointer text-slate-800 rounded-sm min-h-[32px] px-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
      onClick={() => setIsEditing(true)}
    >
      {value || <span className="text-slate-600 italic">{placeholder}</span>}
    </button>
  );
}
